import { useState, useMemo, useEffect, useRef, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useProvince } from '../hooks/useProvince';
import { useStrategyGuidance } from '../hooks/useStrategyGuidance';
import { useToast } from '../hooks/useToast';
import { useAppShellPlatform } from '../hooks/useAppShellPlatform';
import { Spinner } from '../components/Spinner';
import { ErrorMessage } from '../components/ErrorMessage';
import { MobileTrainingShell } from '../components/training/MobileTrainingShell';
import { DesktopTrainingShell } from '../components/training/DesktopTrainingShell';
import { COLORS } from '../constants/theme';
import { formatNumber } from '../utils/format';
import { api } from '../api/client';
import { getRaces, getDraftRates, type RaceConfig, type DraftRateConfig } from '../api/gameConfig';
import { computeTrainOptimize, computeSingleUnitOptimize, mergeTrainQuantities } from '../utils/computeTrainOptimize';
import { TRAINABLE_UNITS } from '../constants/trainableUnits';
import type { Province } from '../types/province';
import type { ActiveStrategyDto } from '../types/strategy';
import type { TrainRequest, TrainResult, DraftRequest, DraftResponse, TrainableUnitType } from '../types/train';

// Fallback unit stats before race config loads
const FALLBACK_UNIT_STATS: Record<string, { offense: number; defense: number }> = {
  OFFENSE_SPECIALIST: { offense: 5, defense: 0 },
  DEFENSE_SPECIALIST: { offense: 0, defense: 5 },
  ELITE: { offense: 8, defense: 2 },
  THIEF: { offense: 0, defense: 0 },
};

const FALLBACK_DRAFT_RATES: DraftRateConfig[] = [
  { id: 'NONE', label: 'None', speedPercent: 0.0, costPerSoldier: 0 },
  { id: 'RESERVIST', label: 'Reservist', speedPercent: 0.3, costPerSoldier: 30 },
  { id: 'NORMAL', label: 'Normal', speedPercent: 0.6, costPerSoldier: 50 },
  { id: 'AGGRESSIVE', label: 'Aggressive', speedPercent: 1.0, costPerSoldier: 75 },
  { id: 'EMERGENCY', label: 'Emergency', speedPercent: 1.5, costPerSoldier: 110 },
];

const DRAFT_LOAD_MESSAGE_DURATION_MS = 5000;

export type TrainingTab = 'train' | 'draft' | 'release' | 'overview';

/**
 * SF-162: mobile is the superset (it already has an extra 'draft' tab desktop doesn't, per SF-141's
 * original split) — `Exclude<>` makes "desktop can't be handed a tab key it has no panel for" a
 * compile-time guarantee, following SF-173's `DesktopStrategyTabKey` precedent exactly (Key
 * Implementation Note 2).
 */
export type DesktopTrainingTab = Exclude<TrainingTab, 'draft'>;

/**
 * SF-141: named, fully-typed props shared by MobileTrainingShell/DesktopTrainingShell — same
 * BuildingsShellProps/ExploreShellProps precedent. State stays hoisted here (not per-shell) so a
 * breakpoint flip mid-edit doesn't lose in-progress trainQty/releaseQty/draft-form drafts, nor the
 * mobileTab selection.
 */
export interface TrainingShellProps {
  province: Province;
  activeStrategy: ActiveStrategyDto | null;
  strategyLoading: boolean;

  // Tab state — two independent enums, not unified (Key Implementation Notes §1)
  desktopActiveTab: DesktopTrainingTab;
  setDesktopActiveTab: (tab: DesktopTrainingTab) => void;
  mobileTab: TrainingTab;
  setMobileTab: (tab: TrainingTab) => void;

  // Train form state
  trainQty: Record<string, number>;
  accelerated: boolean;
  setAccelerated: (value: boolean) => void;
  isSubmitting: boolean;
  submitError: string | null;
  successMessage: string | null;
  bulkMutationVersion: number;

  // Release form state
  releaseQty: Record<string, number>;
  setReleaseQty: (qty: Record<string, number>) => void;

  // Draft settings state
  draftRate: string;
  setDraftRate: (value: string) => void;
  draftTarget: string;
  setDraftTarget: (value: string) => void;
  isSubmittingOptions: boolean;
  optionsError: string | null;
  optionsSuccess: string | null;
  draftLoadMessage: string | null;
  hasDraftUnsavedChanges: boolean;

  // Game config
  raceConfig: RaceConfig | null;
  draftRates: DraftRateConfig[];
  maxDraftTarget: number;

  // Derived values
  totalLand: number;
  totalPop: number;
  /** SF-179 Component 4: `capacityUnitCount`-guarded denominator for Draft %/Target math — distinct
   *  from `totalPop`, which keeps feeding the Peasants-card caption unchanged. */
  draftCapacityPop: number;
  goldAvailable: number;
  soldiers: number;
  peasants: number;
  draftPct: string;
  draftTargetCount: number;
  trainingTime: number;
  hasAnyArmyComp: boolean; // desktop TrainTable's existing gate — unchanged there
  totalCost: number;
  totalTraining: number;

  // Unit helpers
  getTarget: (unitType: TrainableUnitType) => number;
  /** SF-179 Component 2 (Decision 1): differs from `getTarget` only in returning `null` (not `0`)
   *  when there's no active strategy at all — backs Release-to-Strategy's surplus math, which must
   *  distinguish "no strategy" (skip, surplus 0) from "strategy explicitly targets 0" (release in
   *  full), mirroring `computeRazeSurplus`'s convention. */
  getTargetOrNull: (unitType: TrainableUnitType) => number | null;
  getOwnedCount: (unitType: string) => number;
  getInTrainingCount: (unitType: string) => number;
  getTrainingCost: (unitType: string) => number;
  getUnitName: (unitType: string, unitKey: 'offenseSpecialist' | 'defenseSpecialist' | 'elite' | null) => string;
  getUnitStats: (unitType: string, unitKey: 'offenseSpecialist' | 'defenseSpecialist' | 'elite' | null) => { offense: number; defense: number };

  // Handlers
  handleTrainInputChange: (unitType: string, value: string) => void;
  handleOptimizeAll: () => void;
  handleOptimizeSingleUnit: (unitType: TrainableUnitType) => void;
  handleClearAll: () => void;
  handleTrainSubmit: () => Promise<void>;
  handleLoadDraftFromStrategy: () => void;
  handleDraftSave: () => Promise<void>;
  /** SF-179 Component 2 (Decision 6): bumps `bulkMutationVersion` — ReleaseTable calls this after
   *  any of its own external bulk writes (Release to Strategy bulk/per-row, Clear, submit-success)
   *  so its NumericInputs' `resetKey` wiring matches Train's existing convention. */
  handleReleaseBulkMutation: () => void;

  // Mobile-only toast wrappers (platform-conditional, matching Buildings.tsx exactly)
  reportError: (message: string) => void;
  reportSuccess: (message: string) => void;

  worldId: string | null;
  provinceId: string | null;
  refresh: () => Promise<void>;
}

export function Training() {
  const { provinceId, worldId } = useAuth();
  const { province, isLoading, error, refresh } = useProvince();
  const { activeStrategy, isLoading: strategyLoading } = useStrategyGuidance(worldId, provinceId);
  const { addToast } = useToast();
  const platform = useAppShellPlatform();
  const [searchParams] = useSearchParams();

  // SF-162: read-only mount-time seed — resolves CouncilRedirect's fallback case (`/military?tab=
  // overview`) onto the Overview tab instead of the `'train'` default. Read once inside each
  // lazy initializer only; no write-back on tab clicks (deliberately not Buildings.tsx's full
  // two-way `?tab=` sync — see Component 2).
  // Tab state — desktop keeps its own 2-way tab; mobile gets an independent, page-hoisted 3-way tab.
  const [desktopActiveTab, setDesktopActiveTab] = useState<DesktopTrainingTab>(() => (searchParams.get('tab') === 'overview' ? 'overview' : 'train'));
  const [mobileTab, setMobileTab] = useState<TrainingTab>(() => (searchParams.get('tab') === 'overview' ? 'overview' : 'train'));

  // Train form state
  const [trainQty, setTrainQty] = useState<Record<string, number>>({});
  const [accelerated, setAccelerated] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  // SF-141: bumped on every external bulk mutation of trainQty (Fill Strategy all/per-row, Clear,
  // submit-success) so NumericInput's resetKey prop can clear a mid-edit row's stale draft text.
  const [bulkMutationVersion, setBulkMutationVersion] = useState(0);

  // Release form state (lifted for tab state preservation)
  const [releaseQty, setReleaseQty] = useState<Record<string, number>>({});

  // Draft settings state
  const [draftRate, setDraftRate] = useState('NORMAL');
  const [draftTarget, setDraftTarget] = useState('50');
  const [isSubmittingOptions, setIsSubmittingOptions] = useState(false);
  const [optionsError, setOptionsError] = useState<string | null>(null);
  const [optionsSuccess, setOptionsSuccess] = useState<string | null>(null);

  // Action bar inline message for "Load Draft from Strategy"
  const [draftLoadMessage, setDraftLoadMessage] = useState<string | null>(null);
  const draftLoadTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Game config
  const [raceConfig, setRaceConfig] = useState<RaceConfig | null>(null);
  const [draftRates, setDraftRates] = useState<DraftRateConfig[]>(FALLBACK_DRAFT_RATES);
  const [maxDraftTarget, setMaxDraftTarget] = useState(80);

  // Load game config on mount
  useEffect(() => {
    async function loadConfig() {
      const [racesResult, draftResult] = await Promise.all([
        getRaces(),
        getDraftRates(),
      ]);

      if (racesResult.data && province) {
        const race = racesResult.data.races.find(
          (r) => r.id === province.metadata.race.toUpperCase()
        );
        if (race) setRaceConfig(race);
      }

      if (draftResult.data) {
        setDraftRates(draftResult.data.rates);
        setMaxDraftTarget(draftResult.data.maxDraftTarget);
      }
    }
    loadConfig();
  }, [province?.metadata.race]);

  // Sync draft settings from province on load
  useEffect(() => {
    if (province) {
      setDraftRate(province.military.draftRate);
      setDraftTarget(String(province.military.draftTargetPercent));
    }
  }, [province?.military.draftRate, province?.military.draftTargetPercent]);

  // Cleanup draft load timer on unmount
  useEffect(() => {
    return () => {
      if (draftLoadTimerRef.current) {
        clearTimeout(draftLoadTimerRef.current);
      }
    };
  }, []);

  // SF-141 Decision 1: mobile's sticky bottom bar unmounts on success (trainQty clears), which
  // would delete a page-local success message in the same render — so mobile routes feedback
  // through the app-wide toast system instead. Desktop's existing inline messages have no
  // equivalent unmount race, so they're left exactly as before. Train submit path only — Release
  // keeps ReleaseTable's own existing inline handling, Draft keeps its own inline handling too.
  const reportError = useCallback(
    (message: string) => {
      if (platform === 'mobile') {
        addToast(message, 'failure');
      } else {
        setSubmitError(message);
      }
    },
    [platform, addToast]
  );

  const reportSuccess = useCallback(
    (message: string) => {
      if (platform === 'mobile') {
        addToast(message, 'success');
      } else {
        setSuccessMessage(message);
      }
    },
    [platform, addToast]
  );

  // ─── Derived values ──────────────────────────────────────────

  const totalLand = province?.territory.totals.acres ?? 0;
  // SF-179 Component 4 (Decision 5): `totalPop` stays exactly `population.total` — it also feeds
  // DesktopTrainingShell.tsx's Peasants-card caption, which intentionally excludes war horses/
  // generals/the training queue. Draft %/Target math needs the engine's real (wider) denominator
  // instead — see `draftCapacityPop` below. Two names for two meanings, not one repointed variable.
  const totalPop = province?.population.total ?? 0;
  const goldAvailable = province?.resources.gold ?? 0;
  const soldiers = province?.population.soldiers ?? 0;
  const peasants = province?.population.peasants ?? 0;

  // SF-179 Component 4 (Decision 3): `capacityUnitCount` (SF-164, mirrors `getTotalUnitCount()` —
  // the engine's real drafting/overpopulation denominator) is `number | undefined` — a pre-deploy
  // cached DTO can lack it. Guarded exactly as `economyStats.ts:66-71` already guards its own
  // consumer, falling back to the older, narrower `population.total` rather than 0/NaN.
  const draftCapacityPop = Number.isFinite(province?.population.capacityUnitCount)
    ? (province!.population.capacityUnitCount as number)
    : (province?.population.total ?? 0);

  // Draft Level stat card computation
  const draftPct = useMemo(() => {
    if (draftCapacityPop === 0) return '0.0';
    return ((soldiers / draftCapacityPop) * 100).toFixed(1);
  }, [soldiers, draftCapacityPop]);

  // SF-179 Component 4 (Recommended Change #13): the engine's real draft predicate
  // (`MilitaryDraftSystem.java:62,67-69`) effectively ceils against the unfloored target — a
  // player could see "at target" under a `Math.floor` display while the engine still drafts one
  // more soldier. `Math.ceil` matches the engine's actual behavior.
  const draftTargetCount = useMemo(() => {
    if (!province) return 0;
    return Math.ceil(province.military.draftTargetPercent * draftCapacityPop / 100);
  }, [province, draftCapacityPop]);

  // Training time (hardcoded — see SF-70 Implementation Note 6)
  const trainingTime = accelerated ? 12 : 24;

  // Unit helper functions
  const getUnitStats = (unitType: string, unitKey: typeof TRAINABLE_UNITS[0]['key']) => {
    if (unitKey && raceConfig) {
      const unit = raceConfig.units[unitKey];
      return { offense: unit.offense, defense: unit.defense };
    }
    return FALLBACK_UNIT_STATS[unitType] || { offense: 0, defense: 0 };
  };

  const getUnitName = (unitType: string, unitKey: typeof TRAINABLE_UNITS[0]['key']) => {
    if (unitKey && raceConfig) return raceConfig.units[unitKey].name;
    if (unitType === 'THIEF') return 'Thieves';
    return unitType;
  };

  const getTrainingCost = (unitType: string): number => {
    if (!province?.military.trainingCosts) {
      switch (unitType) {
        case 'OFFENSE_SPECIALIST': return 350;
        case 'DEFENSE_SPECIALIST': return 350;
        case 'ELITE': return 800;
        case 'THIEF': return 500;
        default: return 0;
      }
    }
    const costs = province.military.trainingCosts;
    switch (unitType) {
      case 'OFFENSE_SPECIALIST': return costs.offenseSpecialist;
      case 'DEFENSE_SPECIALIST': return costs.defenseSpecialist;
      case 'ELITE': return costs.elite;
      case 'THIEF': return costs.thief;
      default: return 0;
    }
  };

  const getOwnedCount = (unitType: string) => {
    if (!province) return 0;
    switch (unitType) {
      case 'OFFENSE_SPECIALIST': return province.population.offenseSpecialists;
      case 'DEFENSE_SPECIALIST': return province.population.defenseSpecialists;
      case 'ELITE': return province.population.elites;
      case 'THIEF': return province.population.thieves;
      default: return 0;
    }
  };

  const getInTrainingCount = (unitType: string) => {
    if (!province) return 0;
    return province.military.training
      .filter((t: { unitType: string; unitCount: number }) => t.unitType === unitType)
      .reduce((sum: number, t: { unitType: string; unitCount: number }) => sum + t.unitCount, 0);
  };

  // Strategy target computation
  const getTarget = (unitType: TrainableUnitType): number => {
    if (!activeStrategy?.settings) return 0;
    const settings = activeStrategy.settings;
    if (unitType === 'THIEF') {
      return Math.floor((settings.thiefPA ?? 0) * totalLand);
    }
    const keyMap: Record<Exclude<TrainableUnitType, 'THIEF'>, 'offSpec' | 'defSpec' | 'elite'> = {
      OFFENSE_SPECIALIST: 'offSpec',
      DEFENSE_SPECIALIST: 'defSpec',
      ELITE: 'elite',
    };
    return Math.floor((settings.armyComp?.[keyMap[unitType as Exclude<TrainableUnitType, 'THIEF'>]] ?? 0) * totalLand);
  };

  // SF-179 Component 2 (Decision 1): differs from getTarget only in the outer guard — returns null
  // (not 0) when there's no active strategy at all, so Release-to-Strategy's surplus math can tell
  // "no strategy" apart from "strategy explicitly targets 0" (see unitStockMath.ts's
  // computeUnitSurplus).
  const getTargetOrNull = (unitType: TrainableUnitType): number | null => {
    if (!activeStrategy?.settings) return null;
    return getTarget(unitType);
  };

  // Whether the target column should be shown
  // Whether "Optimize All" should be shown
  const hasAnyArmyComp = useMemo(() => {
    if (!activeStrategy?.settings) return false;
    const { armyComp, thiefPA } = activeStrategy.settings;
    return (armyComp.offSpec > 0 || armyComp.defSpec > 0 || armyComp.elite > 0 || (thiefPA ?? 0) > 0);
  }, [activeStrategy]);

  // Total training cost (for bottom bar)
  const totalCost = useMemo(() => {
    return TRAINABLE_UNITS.reduce((sum, u) => {
      const qty = trainQty[u.type] || 0;
      const cost = getTrainingCost(u.type) * (accelerated ? 2 : 1);
      return sum + qty * cost;
    }, 0);
  }, [trainQty, accelerated, province]);

  const totalTraining = useMemo(
    () => Object.values(trainQty).reduce((sum, q) => sum + q, 0),
    [trainQty]
  );

  const hasDraftUnsavedChanges = useMemo(() => {
    if (!province) return false;
    return (
      draftRate !== province.military.draftRate ||
      Number(draftTarget) !== province.military.draftTargetPercent
    );
  }, [draftRate, draftTarget, province]);

  // ─── Handlers ────────────────────────────────────────────────

  const handleOptimizeAll = () => {
    if (!activeStrategy?.settings) return;
    const settings = activeStrategy.settings;

    const owned: Record<TrainableUnitType, number> = {
      OFFENSE_SPECIALIST: getOwnedCount('OFFENSE_SPECIALIST'),
      DEFENSE_SPECIALIST: getOwnedCount('DEFENSE_SPECIALIST'),
      ELITE: getOwnedCount('ELITE'),
      THIEF: getOwnedCount('THIEF'),
    };

    // SF-179 Component 1: previously omitted entirely — Fill Strategy over-queued a second full
    // batch on top of whatever was already in-training, since deficit math never subtracted it.
    const inProgress: Record<TrainableUnitType, number> = {
      OFFENSE_SPECIALIST: Math.floor(getInTrainingCount('OFFENSE_SPECIALIST')),
      DEFENSE_SPECIALIST: Math.floor(getInTrainingCount('DEFENSE_SPECIALIST')),
      ELITE: Math.floor(getInTrainingCount('ELITE')),
      THIEF: Math.floor(getInTrainingCount('THIEF')),
    };

    const costs: Record<TrainableUnitType, number> = {
      OFFENSE_SPECIALIST: getTrainingCost('OFFENSE_SPECIALIST') * (accelerated ? 2 : 1),
      DEFENSE_SPECIALIST: getTrainingCost('DEFENSE_SPECIALIST') * (accelerated ? 2 : 1),
      ELITE: getTrainingCost('ELITE') * (accelerated ? 2 : 1),
      THIEF: getTrainingCost('THIEF') * (accelerated ? 2 : 1),
    };

    const result = computeTrainOptimize({
      armyComp: settings.armyComp,
      thiefPA: settings.thiefPA ?? 0,
      totalLand,
      owned,
      inProgress,
      costs,
      goldBudget: goldAvailable,
      soldierBudget: soldiers,
      peasantBudget: peasants,
    });

    // SF-179 Component 1 idempotency fix: write an explicit 0 for every trainable unit type not in
    // result.quantities (now-at-target), rather than leaving a stale prior draft value — two
    // consecutive Fill Strategy clicks (pre-submit) must produce an identical result.
    setTrainQty((prev) => mergeTrainQuantities(prev, result.quantities, TRAINABLE_UNITS.map((u) => u.type)));
    setBulkMutationVersion((v) => v + 1);
  };

  const handleOptimizeSingleUnit = (unitType: TrainableUnitType) => {
    const target = getTarget(unitType);
    const owned = getOwnedCount(unitType);
    const inProgress = Math.floor(getInTrainingCount(unitType));
    const cost = getTrainingCost(unitType) * (accelerated ? 2 : 1);
    const fill = computeSingleUnitOptimize({
      unitType,
      target,
      ownedCount: owned,
      inProgress,
      cost,
      goldBudget: goldAvailable,
      soldierBudget: soldiers,
      peasantBudget: peasants,
    });
    // SF-179 Component 1 idempotency fix: write fill explicitly (including 0), same
    // mergeTrainQuantities reasoning as the bulk path — netting inProgress into the deficit makes
    // fill===0 reachable in cases it wasn't before (owned alone below target, owned+inProgress at
    // target), and a stale prior draft must not survive that transition.
    setTrainQty((prev) => ({ ...prev, [unitType]: fill }));
    setBulkMutationVersion((v) => v + 1);
  };

  const handleLoadDraftFromStrategy = () => {
    if (!activeStrategy?.settings) return;
    const { draftRate: stratRate, draftLevel } = activeStrategy.settings;
    setDraftRate(stratRate);
    setDraftTarget(String(draftLevel));

    // Show 5-second message
    if (draftLoadTimerRef.current) clearTimeout(draftLoadTimerRef.current);
    const rateLabel = draftRates.find((r) => r.id === stratRate)?.label ?? stratRate;
    setDraftLoadMessage(`Loaded from strategy → ${rateLabel}, ${draftLevel}% (click Save to apply)`);
    draftLoadTimerRef.current = setTimeout(() => {
      setDraftLoadMessage(null);
    }, DRAFT_LOAD_MESSAGE_DURATION_MS);
  };

  const handleTrainInputChange = (unitType: string, value: string) => {
    const numValue = parseInt(value) || 0;
    if (numValue < 0) return;
    setTrainQty((prev) => ({ ...prev, [unitType]: numValue }));
  };

  const handleClearAll = () => {
    setTrainQty({});
    setSubmitError(null);
    setSuccessMessage(null);
    setBulkMutationVersion((v) => v + 1);
  };

  const handleReleaseBulkMutation = () => {
    setBulkMutationVersion((v) => v + 1);
  };

  const handleTrainSubmit = async () => {
    setIsSubmitting(true);
    setSubmitError(null);
    setSuccessMessage(null);

    try {
      const units: Record<string, number> = {};
      Object.entries(trainQty).forEach(([unitType, qty]) => {
        if (qty > 0) units[unitType] = qty;
      });

      if (Object.keys(units).length === 0) {
        reportError('Please enter at least one unit to train');
        setIsSubmitting(false);
        return;
      }

      const request: TrainRequest = { units, accelerated };
      const response = await api.post<TrainResult>(
        `/api/worlds/${worldId}/provinces/${provinceId}/train`,
        request
      );

      if (response.error) {
        reportError(response.error);
      } else if (response.data) {
        const result = response.data;
        const unitsSummary = Object.entries(result.unitsTrained)
          .filter(([, count]) => count > 0)
          .map(([type, count]) => {
            const unitDef = TRAINABLE_UNITS.find((u) => u.type === type);
            return `${Math.floor(count)} ${getUnitName(type, unitDef?.key ?? null)}`;
          })
          .join(', ');

        let message = `Training started: ${unitsSummary} for ${formatNumber(result.trainingCost)} gold.`;
        if (result.daysUntilCompleted > 0) {
          message += ` Units arrive in ${result.daysUntilCompleted} days.`;
        }
        if (result.warnings?.length > 0) {
          message += ` Warnings: ${result.warnings.map((w) => w.message).join('; ')}`;
        }

        reportSuccess(message);
        setTrainQty({});
        setBulkMutationVersion((v) => v + 1);
        await refresh();
      }
    } catch {
      reportError('Failed to submit training order. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDraftSave = async () => {
    setOptionsError(null);
    setOptionsSuccess(null);

    const draftTargetNum = parseInt(draftTarget) || 0;
    if (draftTargetNum < 0 || draftTargetNum > maxDraftTarget) {
      setOptionsError(`Draft target must be between 0 and ${maxDraftTarget}%`);
      return;
    }

    setIsSubmittingOptions(true);
    try {
      const request: DraftRequest = {
        draftRate,
        draftTargetPercent: draftTargetNum,
      };
      const response = await api.post<DraftResponse>(
        `/api/worlds/${worldId}/provinces/${provinceId}/draft`,
        request
      );

      if (response.error) {
        setOptionsError(response.error);
      } else if (response.data) {
        if (response.data.result.success) {
          setOptionsSuccess('Draft settings updated');
        } else {
          setOptionsError(response.data.result.failureReason || 'Failed to update draft settings');
        }
      }
    } catch {
      setOptionsError('Failed to update draft settings. Please try again.');
    } finally {
      setIsSubmittingOptions(false);
    }
  };

  // ─── Render ──────────────────────────────────────────────────

  if (isLoading) {
    return (
      <div className="flex justify-center items-center min-h-screen" style={{ backgroundColor: COLORS.bg }}>
        <Spinner size="lg" />
      </div>
    );
  }

  if (error || !province) {
    return (
      <div className="min-h-screen px-4 py-8" style={{ backgroundColor: COLORS.bg }}>
        <ErrorMessage message={error || 'Failed to load province'} />
      </div>
    );
  }

  const shellProps: TrainingShellProps = {
    province,
    activeStrategy,
    strategyLoading,
    desktopActiveTab,
    setDesktopActiveTab,
    mobileTab,
    setMobileTab,
    trainQty,
    accelerated,
    setAccelerated,
    isSubmitting,
    submitError,
    successMessage,
    bulkMutationVersion,
    releaseQty,
    setReleaseQty,
    draftRate,
    setDraftRate,
    draftTarget,
    setDraftTarget,
    isSubmittingOptions,
    optionsError,
    optionsSuccess,
    draftLoadMessage,
    hasDraftUnsavedChanges,
    raceConfig,
    draftRates,
    maxDraftTarget,
    totalLand,
    totalPop,
    draftCapacityPop,
    goldAvailable,
    soldiers,
    peasants,
    draftPct,
    draftTargetCount,
    trainingTime,
    hasAnyArmyComp,
    totalCost,
    totalTraining,
    getTarget,
    getTargetOrNull,
    getOwnedCount,
    getInTrainingCount,
    getTrainingCost,
    getUnitName,
    getUnitStats,
    handleTrainInputChange,
    handleOptimizeAll,
    handleOptimizeSingleUnit,
    handleClearAll,
    handleTrainSubmit,
    handleLoadDraftFromStrategy,
    handleDraftSave,
    handleReleaseBulkMutation,
    reportError,
    reportSuccess,
    worldId,
    provinceId,
    refresh,
  };

  return platform === 'mobile'
    ? <MobileTrainingShell {...shellProps} />
    : <DesktopTrainingShell {...shellProps} />;
}
